import { safeFetch } from "../utils/safeFetch";

const readErrorMessage = async (response) => {
  const fallback = `HTTP error! status: ${response.status}`;
  try {
    const payload = await response.json();
    if (typeof payload?.error === "string") return payload.error;
    if (typeof payload?.message === "string") return payload.message;
    return fallback;
  } catch (_) {
    const text = await response.text().catch(() => "");
    return text || fallback;
  }
};

const localBackendBaseUrl = () => {
  const configured = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
  if (configured) return configured;
  if (typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname)) {
    return "http://localhost:3001";
  }
  return "";
};

const parseHeliteDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const text = String(value).trim();
  if (!text) return null;

  const isoMatch = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const italianMatch = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (italianMatch) {
    const [, day, month, year] = italianMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime())
    ? null
    : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const startOfLocalDay = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  const validDate = Number.isNaN(date.getTime()) ? new Date() : date;
  return new Date(validDate.getFullYear(), validDate.getMonth(), validDate.getDate());
};

const isActiveHeliteGuest = (guest, today = new Date()) => {
  const arrival = parseHeliteDate(guest?.arrival);
  const departure = parseHeliteDate(guest?.departure);
  if (!arrival || !departure) return false;

  const currentDay = startOfLocalDay(today);
  return arrival <= currentDay && currentDay <= departure;
};

const buildHeliteGuestKey = (guest) => {
  const room = String(guest?.roomnr || guest?.roomNumber || guest?.heliteRoomNumber || "").trim().toLowerCase();
  const id = String(guest?.id || guest?.billId || guest?.heliteBillId || "").trim();
  return room || (id ? `id:${id}` : "");
};

const getHeliteGuestQualityScore = (guest) => {
  const holderName = String(guest?.holderName || "").trim();
  const isPlaceholderName = !holderName || /\bpax\b/i.test(holderName) || /^dep[\s-]*\d*$/i.test(holderName);
  const departure = parseHeliteDate(guest?.departure);
  const departureTime = departure ? departure.getTime() / 100000000 : 0;

  return [
    isActiveHeliteGuest(guest) ? 10000 : 0,
    departureTime,
    isPlaceholderName ? 0 : 100,
    holderName.length,
    guest?.holderEmail ? 10 : 0,
    guest?.boardBasis ? 10 : 0,
    guest?.id ? 1 : 0,
  ].reduce((sum, value) => sum + value, 0);
};

const normalizeHeliteGuests = (guests = [], options = {}) => {
  const activeOnly = Boolean(options.activeOnly);
  const guestsByKey = new Map();

  (Array.isArray(guests) ? guests : []).forEach((guest) => {
    if (activeOnly && !isActiveHeliteGuest(guest)) return;

    const key = buildHeliteGuestKey(guest);
    const existing = guestsByKey.get(key);

    if (!existing || getHeliteGuestQualityScore(guest) > getHeliteGuestQualityScore(existing)) {
      guestsByKey.set(key, guest);
    }
  });

  return [...guestsByKey.values()];
};

export const getHeliteCheckedInGuests = async (token, tenant, options = {}) => {
  const activeOnly = Boolean(options.activeOnly);
  const query = activeOnly ? "?activeOnly=true" : "";
  const response = await safeFetch(`/api/helite/guests/checkin${query}`, {
    method: "GET",
    headers: {
      accept: "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  const payload = await response.json();
  return {
    ...payload,
    guests: normalizeHeliteGuests(payload?.guests, { activeOnly }),
  };
};

export const getHeliteChargeCodes = async (token, tenant, structCode = "") => {
  const query = structCode ? `?struct_code=${encodeURIComponent(structCode)}` : "";
  const requestOptions = {
    method: "GET",
    headers: {
      accept: "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    cache: "no-store",
  };
  let response = await safeFetch(`/api/helite/charge-codes${query}`, requestOptions);

  if (response.status === 404) {
    const baseUrl = localBackendBaseUrl();
    if (baseUrl) {
      response = await safeFetch(`${baseUrl}/api/helite/charge-codes${query}`, requestOptions);
    }
  }

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  return response.json();
};

const heliteRequest = async (path, token, tenant, options = {}) => {
  const requestOptions = {
    ...options,
    headers: {
      accept: "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    },
    cache: "no-store",
  };

  let response = await safeFetch(`/api/helite${path}`, requestOptions);

  if (response.status === 404) {
    const baseUrl = localBackendBaseUrl();
    if (baseUrl) {
      response = await safeFetch(`${baseUrl}/api/helite${path}`, requestOptions);
    }
  }

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  return response;
};

export const getHeliteServiceMappings = async (token, tenant) => {
  const response = await heliteRequest("/service-mappings", token, tenant, {
    method: "GET",
  });
  const payload = await response.json();
  return Array.isArray(payload?.mappings) ? payload.mappings : [];
};

export const saveHeliteServiceMapping = async (token, tenant, mapping) => {
  const response = await heliteRequest("/service-mappings", token, tenant, {
    method: "POST",
    body: JSON.stringify(mapping),
  });
  return response.json();
};

export const deleteHeliteServiceMapping = async (token, tenant, id) => {
  await heliteRequest(`/service-mappings/${encodeURIComponent(id)}`, token, tenant, {
    method: "DELETE",
  });
};

export const deductHeliteCharge = async (debitId, token, tenant) => {
  const response = await heliteRequest(`/charges/${encodeURIComponent(debitId)}/deduct`, token, tenant, {
    method: "POST",
  });
  return response.json();
};

export const restoreHeliteCharge = async (chargeId, token, tenant) => {
  const response = await heliteRequest(`/charges/${encodeURIComponent(chargeId)}/restore`, token, tenant, {
    method: "POST",
  });
  return response.json();
};

export const getHeliteBuono = async (buonoId, token, tenant) => {
  const response = await heliteRequest(`/buoni/${encodeURIComponent(buonoId)}`, token, tenant, {
    method: "GET",
  });
  const payload = await response.json();
  return payload?.buono || null;
};

export const signHeliteBuono = async (buonoId, signature, token, tenant) => {
  const response = await heliteRequest(`/buoni/${encodeURIComponent(buonoId)}/sign`, token, tenant, {
    method: "POST",
    body: JSON.stringify(signature),
  });
  const payload = await response.json();
  return payload?.buono || null;
};

export const printHeliteBuono = async (buonoId, token, tenant) => {
  const response = await heliteRequest(`/buoni/${encodeURIComponent(buonoId)}/print`, token, tenant, {
    method: "POST",
  });
  return response.json();
};
